import { Link } from "react-router-dom";
import { ArrowRight, Download } from "lucide-react";
import { useSeo } from "../lib/seo";
import { IMAGES, PATHWAYS, SERVICES, FAQS, BUSINESS } from "../data/content";
import { Marquee } from "../components/site/Sections";
import { ServiceIcon } from "../components/site/Icons";
import { LogoMark } from "../components/site/LogoMark";
import { Testimonials } from "../components/site/Testimonials";
import { FeaturedResource, useFeaturedResource } from "../components/site/FeaturedResource";
import { DownloadDialog } from "../components/forms/DownloadDialog";
import { FaqList } from "../components/site/FaqList";

const Hero = () => (
  <section className="bg-cream text-ink" data-testid="home-hero">
    <div className="max-w-6xl mx-auto px-6 py-16 md:py-28 grid md:grid-cols-[1fr_1.1fr] gap-12 items-center">
      <div className="min-w-0">
        <div className="fd mb-8 inline-block rounded-full shadow-md" data-testid="hero-logo">
          <LogoMark size={140} className="w-28 h-28 md:w-[140px] md:h-[140px]" />
        </div>
        <p className="lbl text-teal fd f1">For schools, families &amp; educators</p>
        <h1 className="mt-6 font-display">
          <span className="ln"><span>Deaf education,</span></span>
          <span className="ln"><span><em>from both sides.</em></span></span>
        </h1>
        <p className="mt-6 max-w-md fd f1 text-lg">Over 25 years teaching children with hearing loss, certified Teacher of D/HH children, PreK-12 and Special Education Administrator. Visual Phonics Trainer, Guest Speaker and Business Owner.</p>
        <p className="mt-3 max-w-md fd f1 text-slate">{BUSINESS.licensing}. Working with clients worldwide, in person or virtually.</p>
        <div className="mt-8 flex flex-wrap items-center gap-4 fd f2">
          <Link to="/contact#consultation" className="group inline-flex items-center gap-3 font-sans bg-teal text-paper px-6 py-4 rounded-full hover:bg-teal-deep transition-colors" data-testid="hero-consultation-cta">
            Request a consultation <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" aria-hidden="true" />
          </Link>
          <Link to="/book" className="font-sans link-u py-1" data-testid="hero-book-link">Book an appointment</Link>
          <Link to="/contact#quote" className="font-sans link-u py-1" data-testid="hero-quote-link">Request a quote</Link>
        </div>
      </div>
      <div className="tilt relative">
        <figure className="wp relative aspect-[4/5] bg-sky-haze rounded-t-[999px] overflow-hidden">
          <img src={IMAGES.portrait.src} alt={IMAGES.portrait.alt} className="absolute inset-0 w-full h-full object-cover object-top" fetchpriority="high" />
          <figcaption className="absolute left-4 bottom-4 lbl bg-paper/90 text-ink px-3 py-1.5 rounded-full">Michelle Andros, Owner</figcaption>
        </figure>
      </div>
    </div>
  </section>
);

const Pathways = ({ resource }) => (
  <section id="pathways" className="bg-cream text-ink py-24 md:py-32" aria-labelledby="pathways-title" data-testid="home-pathways">
    <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-[1fr_2fr] gap-12">
      <div>
        <h2 id="pathways-title" className="font-display">Who I work with</h2>
        <p className="mt-6 text-slate max-w-xs">Choose your path to see how I can help, then tell me a little about your student or team.</p>
      </div>
      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-6 st">
        {PATHWAYS.map((p, i) => (
          <li key={p.key} className={i % 2 ? "sm:mt-12" : ""}>
            <Link to={`/contact?role=${encodeURIComponent(p.role)}#consultation`} className="group block" data-testid={`pathway-${p.key}-link`}>
              <figure className="aspect-[4/3] sm:aspect-[3/4] bg-sand overflow-hidden relative">
                <img src={p.img} alt={p.alt} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <figcaption className="absolute left-3 bottom-3 lbl bg-paper/95 text-ink px-3 py-1.5 rounded-full">{p.title}</figcaption>
              </figure>
              <p className="mt-4 text-[17px]">{p.text}</p>
              <span className="mt-2 inline-flex items-center gap-2 font-sans text-sm text-teal">Start here <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" aria-hidden="true" /></span>
            </Link>
            {p.key === "families" && resource && (
              <DownloadDialog resource={resource} idPrefix="family" trigger={
                <button className="mt-3 inline-flex items-center gap-2 font-sans text-sm bg-paper border border-teal text-teal rounded-full px-4 py-2 hover:bg-teal hover:text-paper transition-colors text-left" data-testid="pathway-families-resource-button">
                  <Download className="w-4 h-4 shrink-0" aria-hidden="true" /> Free download: {resource.title}
                </button>
              } />
            )}
          </li>
        ))}
      </ul>
    </div>
  </section>
);

const ServicesSummary = () => (
  <section className="bg-sky text-ink py-24 md:py-32" aria-labelledby="services-title" data-testid="home-services">
    <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-[1.1fr_1fr] gap-12">
      <div className="min-w-0">
        <h2 id="services-title" className="font-display">Services</h2>
        <ul className="mt-12 space-y-6">
          {SERVICES.map((s) => (
            <li key={s.slug}>
              <Link to={`/services#${s.slug}`} className="flex gap-4 group" data-testid={`home-service-${s.slug}`}>
                <ServiceIcon name={s.icon} />
                <span className="font-display text-xl link-u">{s.title}</span>
              </Link>
            </li>
          ))}
        </ul>
        <p className="lbl mt-12 text-teal">In person or virtual, depending on your location</p>
      </div>
      <figure className="aspect-[3/4] bg-sky-mist relative overflow-hidden">
        <img src={IMAGES.services.src} alt={IMAGES.services.alt} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
        <figcaption className="absolute left-4 bottom-4 lbl bg-paper/95 text-ink px-3 py-1.5 rounded-full">Visual Phonics and literacy, live and virtual</figcaption>
      </figure>
    </div>
  </section>
);

const AboutTeaser = () => (
  <section className="bg-cream text-ink py-24 md:py-32" aria-labelledby="about-title" data-testid="home-about">
    <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-[1fr_1.2fr] gap-12 items-center">
      <figure className="aspect-square max-w-md w-full bg-sand rounded-full overflow-hidden relative">
        <img src={IMAGES.portrait.src} alt={IMAGES.portrait.alt} loading="lazy" className="absolute inset-0 w-full h-full object-cover object-top" />
      </figure>
      <div className="min-w-0">
        <p className="lbl text-teal">Meet your consultant</p>
        <h2 id="about-title" className="font-display mt-4">Michelle Andros</h2>
        <p className="mt-6 max-w-prose">I have taught Deaf and Hard of Hearing students in the classroom and as an itinerant teacher, consulted statewide, and led programs. Now I help the schools, families and professionals around each student.</p>
        <ul className="mt-6 space-y-2 text-[17px]">
          {["Certified teacher of the Deaf, sign language proficient", "Understands both the school side and the family side of the IEP process", "See the Sound Visual Phonics Trainer", "Ambassador to Eduplex Institute in South Africa"].map((t) => (
            <li key={t} className="flex gap-3"><span className="mt-2.5 w-2 h-2 rounded-full bg-brass shrink-0" aria-hidden="true" />{t}</li>
          ))}
        </ul>
        <Link to="/about" className="mt-8 inline-flex items-center gap-2 font-sans link-u text-teal" data-testid="home-about-link">Read my story and credentials <ArrowRight className="w-4 h-4" aria-hidden="true" /></Link>
      </div>
    </div>
  </section>
);

const BookingTeaser = () => (
  <section className="bg-teal text-paper py-24 md:py-32" data-testid="home-booking">
    <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center">
      <div>
        <h2 className="font-display">Book an appointment</h2>
        <p className="mt-6 max-w-md">Choose an open time on my booking calendar. Google sends you a confirmation right away.</p>
        <p className="lbl mt-6 text-sky">Travel season: June, July, August</p>
      </div>
      <div className="bg-paper text-ink rounded-2xl p-8">
        <p className="font-display text-2xl">Pick a time that works for you</p>
        <p className="mt-3 text-slate">See open dates, choose a time, and tell me what you'd like to talk about.</p>
        <Link to="/book" className="mt-6 inline-flex w-full justify-center font-sans bg-teal text-paper rounded-full py-3.5 hover:bg-teal-deep transition-colors" data-testid="home-book-cta">See open times</Link>
      </div>
    </div>
  </section>
);

const FaqTeaser = () => (
  <section className="bg-cream text-ink py-24 md:py-32" aria-labelledby="faq-title" data-testid="home-faq">
    <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-[1fr_2fr] gap-12">
      <div>
        <h2 id="faq-title" className="font-display">Questions</h2>
        <Link to="/faq" className="mt-6 inline-flex font-sans link-u text-teal" data-testid="home-faq-link">All questions</Link>
      </div>
      <FaqList items={FAQS.slice(0, 4)} prefix="home" />
    </div>
  </section>
);

const ContactBand = () => (
  <section className="bg-sky text-ink py-24 md:py-28" data-testid="home-contact-band">
    <div className="max-w-6xl mx-auto px-6">
      <h2 className="font-display max-w-2xl">Let's talk about your student</h2>
      <div className="mt-10 flex flex-wrap gap-4 font-sans">
        <Link to="/contact#consultation" className="bg-teal text-paper rounded-full px-8 py-4 hover:bg-teal-deep transition-colors" data-testid="band-consultation-cta">Request a consultation</Link>
        <Link to="/book" className="border-2 border-teal text-teal rounded-full px-6 py-3.5 hover:bg-teal hover:text-paper transition-colors" data-testid="band-book-cta">Book an appointment</Link>
        <Link to="/contact#quote" className="border-2 border-teal text-teal rounded-full px-6 py-3.5 hover:bg-teal hover:text-paper transition-colors" data-testid="band-quote-cta">Request a quote</Link>
      </div>
    </div>
  </section>
);

export default function Home() {
  const featured = useFeaturedResource();
  useSeo(null, "Deaf Education Consulting LLC: consultation, IEP support, assessments, staff training, Visual Phonics and family guidance for Deaf and Hard of Hearing students. Licensed in Pennsylvania, serving clients worldwide.");
  return (
    <>
      <Hero />
      <Marquee />
      <Pathways resource={featured} />
      <FeaturedResource resource={featured} />
      <Testimonials />
      <ServicesSummary />
      <AboutTeaser />
      <BookingTeaser />
      <FaqTeaser />
      <ContactBand />
    </>
  );
}
